import { useEffect, useMemo, useState, type ReactNode } from 'react';

import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import AdminPageShell from '../AdminPageShell';
import AdminPagination from '../AdminPagination';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import AdminToast from '../AdminToast';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import { formatMoney } from '../../../utils/currency';
import { isAdministrateur } from '../../../utils/adminAccess';
import { deleteAdminApprenant, deleteAdminInscription } from '../../../services/academiaAdminService';

export type InscriptionRow = {
  id: string;
  rowKind?: 'compte' | 'inscription';
  statut: string;
  matriculeSnapshot?: string;
  categorieSnapshot?: string;
  montantTotalCdf?: number | string;
  montantPayeCdf?: number | string;
  dateInscription?: string;
  apprenant?: {
    id?: string;
    telephone?: string;
    email?: string;
    profil?: { prenom?: string; nom?: string };
  };
  filiere?: { libelle?: string; code?: string } | null;
  cohorte?: { code?: string; libelle?: string } | null;
  categorisationRaison?: string | null;
};

type Props = {
  user?: { role?: string } | null;
  enAttente: InscriptionRow[];
  actives: InscriptionRow[];
  isLoading: boolean;
  isError: boolean;
  onRefresh: () => void;
};

type StatusFilter = 'all' | 'pending' | 'active';

const STATUT_LABELS: Record<string, string> = {
  COMPTE_SANS_FORMATION: 'Compte sans formation',
  BROUILLON: 'Brouillon',
  EN_ATTENTE_PAIEMENT: 'En attente de paiement',
  PARTIELLEMENT_PAYEE: 'Partiellement payée',
  CONFIRMEE: 'Confirmée',
  EN_COURS: 'En cours',
  TERMINEE: 'Terminée',
  DELIBEREE: 'Délibérée',
  BREVET_DELIVRE: 'Brevet délivré',
  ANNULEE: 'Annulée',
  DESISTEE: 'Désistée',
};

const FILTER_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'Toutes' },
  { id: 'pending', label: 'En attente' },
  { id: 'active', label: 'Actives' },
];

function isCompteRow(row: InscriptionRow) {
  return row.rowKind === 'compte' || row.statut === 'COMPTE_SANS_FORMATION';
}

function toNumber(value: number | string | undefined) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function formatName(row: InscriptionRow) {
  const p = row.apprenant?.profil;
  const name = [p?.prenom, p?.nom].filter(Boolean).join(' ');
  return name || row.apprenant?.telephone || '—';
}

function formatStatut(statut: string) {
  return STATUT_LABELS[statut] ?? statut.replace(/_/g, ' ').toLowerCase();
}

function statutClass(row: InscriptionRow) {
  if (isCompteRow(row)) return 'bg-amber-100 text-amber-800';
  if (row.statut === 'CONFIRMEE' || row.statut === 'EN_COURS' || row.statut === 'TERMINEE') {
    return 'bg-emerald-100 text-emerald-800';
  }
  if (row.statut === 'PARTIELLEMENT_PAYEE' || row.statut === 'EN_ATTENTE_PAIEMENT') {
    return 'bg-amber-100 text-amber-800';
  }
  if (row.statut === 'ANNULEE' || row.statut === 'DESISTEE') return 'bg-slate-100 text-slate-600';
  return 'bg-emerald-100 text-emerald-800';
}

function rowsForFilter(enAttente: InscriptionRow[], actives: InscriptionRow[], filter: StatusFilter) {
  if (filter === 'pending') return enAttente;
  if (filter === 'active') return actives;
  return [...enAttente, ...actives];
}

function paymentRatio(row: InscriptionRow) {
  const total = toNumber(row.montantTotalCdf);
  const paid = toNumber(row.montantPayeCdf);
  if (total <= 0) return paid > 0 ? 100 : 0;
  return Math.min(100, Math.round((paid / total) * 100));
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg bg-surface-container-low p-3">
      <dt className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-on-surface">{value}</dd>
    </div>
  );
}

function InscriptionDetailModal({ row, onClose }: { row: InscriptionRow; onClose: () => void }) {
  const compteOnly = isCompteRow(row);
  const total = toNumber(row.montantTotalCdf);
  const paid = toNumber(row.montantPayeCdf);
  const reste = Math.max(0, total - paid);
  const ratio = paymentRatio(row);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="presentation"
      onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="inscription-modal-title"
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-outline-variant/15 bg-surface-container-lowest p-5">
          <div>
            <h3 id="inscription-modal-title" className="text-lg font-black text-primary">
              Dossier d&apos;inscription
            </h3>
            <p className="mt-1 font-mono text-xs text-on-surface-variant">{row.matriculeSnapshot || '—'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high"
            aria-label="Fermer">
            <LucideSymbolIcon name="close" tone="inherit" size={20} />
          </button>
        </div>

        <div className="space-y-6 p-5">
          <section>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">Apprenant</h4>
            <dl className="grid gap-3 sm:grid-cols-2">
              <DetailItem label="Nom complet" value={formatName(row)} />
              <DetailItem label="Téléphone" value={row.apprenant?.telephone || '—'} />
              <DetailItem label="E-mail" value={row.apprenant?.email || '—'} />
              <DetailItem label="Matricule" value={<span className="font-mono">{row.matriculeSnapshot || '—'}</span>} />
              <DetailItem
                label="Catégorie"
                value={row.categorieSnapshot?.replace(/_/g, ' ') || '—'}
              />
              <DetailItem label="Raison catégorisation" value={row.categorisationRaison || '—'} />
            </dl>
          </section>

          {compteOnly ? (
            <section>
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">Formation</h4>
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Cet apprenant a créé un compte mais n&apos;est pas encore inscrit à une formation.
              </p>
            </section>
          ) : (
            <section>
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">Formation</h4>
              <dl className="grid gap-3 sm:grid-cols-2">
                <DetailItem label="Filière" value={row.filiere?.libelle || row.filiere?.code || '—'} />
                <DetailItem label="Code filière" value={row.filiere?.code || '—'} />
                <DetailItem label="Cohorte" value={row.cohorte?.libelle || row.cohorte?.code || '—'} />
                <DetailItem label="Code cohorte" value={row.cohorte?.code || '—'} />
              </dl>
            </section>
          )}

          <section>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
              {compteOnly ? 'Statut du compte' : 'Statut & paiement'}
            </h4>
            <dl className="grid gap-3 sm:grid-cols-2">
              <DetailItem
                label="Statut dossier"
                value={
                  <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${statutClass(row)}`}>
                    {formatStatut(row.statut)}
                  </span>
                }
              />
              {compteOnly ? (
                <DetailItem label="Compte créé le" value={formatDate(row.dateInscription)} />
              ) : (
                <>
                  <DetailItem label="Progression paiement" value={`${ratio} %`} />
                  <DetailItem label="Montant total" value={formatMoney(total, 'CDF')} />
                  <DetailItem label="Montant payé" value={formatMoney(paid, 'CDF')} />
                  <DetailItem
                    label="Reste à payer"
                    value={
                      <span className={reste > 0 ? 'font-semibold text-amber-700' : 'text-emerald-700'}>
                        {formatMoney(reste, 'CDF')}
                      </span>
                    }
                  />
                  <DetailItem label="Date d'inscription" value={formatDate(row.dateInscription)} />
                </>
              )}
            </dl>
            {!compteOnly ? (
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-surface-container-high">
                <div
                  className={[
                    'h-full rounded-full transition-all',
                    ratio >= 100 ? 'bg-emerald-500' : ratio > 0 ? 'bg-primary' : 'bg-outline-variant/40',
                  ].join(' ')}
                  style={{ width: `${ratio}%` }}
                />
              </div>
            ) : null}
          </section>
        </div>

        <div className="flex justify-end border-t border-outline-variant/15 p-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white hover:opacity-90">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  helper,
  icon,
  tone = 'default',
}: {
  title: string;
  value: string;
  helper: string;
  icon: string;
  tone?: 'default' | 'warning' | 'success' | 'primary';
}) {
  const iconBg =
    tone === 'warning'
      ? 'bg-amber-100 text-amber-700'
      : tone === 'success'
        ? 'bg-emerald-100 text-emerald-700'
        : tone === 'primary'
          ? 'bg-primary/10 text-primary'
          : 'bg-surface-container text-on-surface-variant';

  return (
    <div className="rounded-xl border border-outline-variant/15 bg-white p-4 shadow-sm">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${iconBg}`}>
        <LucideSymbolIcon name={icon} tone="inherit" size={20} />
      </div>
      <p className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{title}</p>
      <p className="mt-1 text-2xl font-black text-primary">{value}</p>
      <p className="mt-1 text-[11px] text-on-surface-variant">{helper}</p>
    </div>
  );
}

export default function AcademiaInscriptionsPanel({
  user,
  enAttente,
  actives,
  isLoading,
  isError,
  onRefresh,
}: Props) {
  useRegisterPortalRefresh(onRefresh);
  const canDelete = isAdministrateur(user);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [detailRow, setDetailRow] = useState<InscriptionRow | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);

  const stats = useMemo(() => {
    let totalCdf = 0;
    let paidCdf = 0;
    for (const row of actives) {
      totalCdf += toNumber(row.montantTotalCdf);
      paidCdf += toNumber(row.montantPayeCdf);
    }
    const reste = Math.max(0, totalCdf - paidCdf);
    return {
      total: enAttente.length + actives.length,
      pending: enAttente.length,
      active: actives.length,
      totalCdf,
      paidCdf,
      reste,
    };
  }, [enAttente, actives]);

  const filteredItems = useMemo(
    () => rowsForFilter(enAttente, actives, statusFilter),
    [enAttente, actives, statusFilter],
  );

  const {
    paginatedItems,
    currentPage,
    totalPages,
    onPageChange,
    summary: paginationSummary,
  } = useAdminTablePagination(filteredItems);

  const sideCard = useMemo(
    () => ({
      label: 'Encaissements (page)',
      value: isLoading ? '…' : formatMoney(stats.paidCdf, 'CDF'),
      helper: isLoading
        ? null
        : `${stats.total} dossier(s) · ${formatMoney(stats.reste, 'CDF')} restant à solder`,
      sideLinks: [{ label: 'Apprenants', to: '/admin/apprenants', icon: 'group' }],
    }),
    [isLoading, stats],
  );

  const heroPrimaryAction =
    stats.pending > 0 ? (
      <button
        type="button"
        onClick={() => setStatusFilter('pending')}
        className="inline-flex rounded-lg bg-white px-4 py-2 text-xs font-bold text-primary"
      >
        {stats.pending} compte{stats.pending > 1 ? 's' : ''} sans formation
      </button>
    ) : (
      <span className="inline-flex rounded-lg bg-white/90 px-4 py-2 text-xs font-bold text-primary">
        Aucun compte en attente
      </span>
    );

  return (
    <AdminPageShell
      title="Liste des inscriptions"
      description={
        isLoading || isError
          ? null
          : `${stats.pending} compte(s) sans formation · ${stats.active} inscription(s) avec paiement.`
      }
      sideCard={sideCard}
      onRetry={onRefresh}
      isLoading={isLoading}
      isError={isError}
      loadingMessage="Chargement des inscriptions…"
      errorMessage="Impossible de charger les inscriptions."
      primaryAction={!isLoading && !isError ? heroPrimaryAction : undefined}
    >
      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={() => setToast(null)}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <SummaryCard
          title="Total"
          value={String(stats.total)}
          helper="Comptes sans formation + inscriptions payées"
          icon="assignment"
          tone="primary"
        />
        <SummaryCard
          title="En attente"
          value={String(stats.pending)}
          helper="Compte créé, aucune formation choisie"
          icon="schedule"
          tone="warning"
        />
        <SummaryCard
          title="Actives"
          value={String(stats.active)}
          helper="Au moins une inscription formation payée"
          icon="check_circle"
          tone="success"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtrer par statut">
          {FILTER_OPTIONS.map((opt) => {
            const count =
              opt.id === 'all'
                ? stats.total
                : opt.id === 'pending'
                  ? stats.pending
                  : stats.active;
            const active = statusFilter === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStatusFilter(opt.id)}
                className={[
                  'rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'border border-outline-variant/25 bg-white text-slate-700 hover:bg-surface-container-low',
                ].join(' ')}
              >
                {opt.label}
                <span className={`ml-1.5 text-xs ${active ? 'text-white/80' : 'text-on-surface-variant'}`}>
                  ({count})
                </span>
              </button>
            );
          })}
        </div>
        <p className="text-sm text-on-surface-variant">
          {filteredItems.length} résultat{filteredItems.length !== 1 ? 's' : ''}
          {statusFilter !== 'all' ? ` — filtre « ${FILTER_OPTIONS.find((o) => o.id === statusFilter)?.label} »` : ''}
        </p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-outline-variant/20 bg-surface-container-lowest shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-outline-variant/15 bg-surface-container-low text-xs uppercase tracking-wide text-on-surface-variant">
            <tr>
              <th className="px-4 py-3">Apprenant</th>
              <th className="px-4 py-3">Formation</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Paiement</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center">
                  <p className="font-medium text-on-surface">Aucune inscription à afficher</p>
                  <p className="mt-1 text-sm text-on-surface-variant">
                    {stats.total === 0
                      ? 'Les apprenants apparaîtront ici dès la création de compte ou le paiement d’une inscription.'
                      : 'Modifiez le filtre ou la recherche dans la barre du haut.'}
                  </p>
                </td>
              </tr>
            ) : (
              paginatedItems.map((row) => {
                const compteOnly = isCompteRow(row);
                const total = toNumber(row.montantTotalCdf);
                const paid = toNumber(row.montantPayeCdf);
                const reste = Math.max(0, total - paid);
                const ratio = paymentRatio(row);
                return (
                  <tr key={row.id} className="border-b border-outline-variant/10 last:border-0 hover:bg-surface-container-low/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{formatName(row)}</div>
                      <div className="mt-0.5 font-mono text-[11px] text-on-surface-variant">
                        {row.matriculeSnapshot || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-on-surface-variant">
                      {compteOnly ? (
                        <span className="text-sm italic text-on-surface-variant">Aucune formation</span>
                      ) : (
                        <>
                          <div className="max-w-[200px] truncate font-medium text-slate-800">
                            {row.filiere?.libelle || row.filiere?.code || '—'}
                          </div>
                          <div className="text-xs">{row.cohorte?.code || row.cohorte?.libelle || '—'}</div>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${statutClass(row)}`}>
                        {formatStatut(row.statut)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {compteOnly ? (
                        <span className="text-sm text-on-surface-variant">—</span>
                      ) : (
                        <>
                          <span className={`text-sm font-semibold ${reste > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                            {ratio}%
                          </span>
                          <p className="text-[11px] text-on-surface-variant">
                            {reste > 0 ? `${formatMoney(reste, 'CDF')} restant` : 'Soldé'}
                          </p>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setDetailRow(row)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/10">
                          <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
                          Voir
                        </button>
                        <AdminTableDeleteButton
                          canDelete={canDelete}
                          disabled={deletingId === row.id}
                          confirmMessage={
                            compteOnly
                              ? `Supprimer définitivement le compte de ${formatName(row)} ? Cette action est irréversible.`
                              : `Supprimer définitivement l'inscription de ${formatName(row)} ? Cette action est irréversible.`
                          }
                          onDelete={async () => {
                            setDeletingId(row.id);
                            try {
                              let response: { message?: string } | undefined;
                              if (compteOnly) {
                                const apprenantId = row.apprenant?.id;
                                if (!apprenantId) throw new Error('Compte apprenant introuvable.');
                                response = await deleteAdminApprenant(apprenantId);
                              } else {
                                response = await deleteAdminInscription(row.id);
                              }
                              await onRefresh();
                              setToast({
                                message:
                                  response?.message ||
                                  (compteOnly ? 'Compte apprenant supprimé.' : 'Inscription supprimée.'),
                                variant: 'success',
                              });
                            } catch (err) {
                              setToast({
                                message: err instanceof Error ? err.message : 'Suppression impossible.',
                                variant: 'error',
                              });
                            } finally {
                              setDeletingId(null);
                            }
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {filteredItems.length > 0 ? (
          <AdminPagination
            className="border-t border-outline-variant/15 px-4 py-3"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            summary={paginationSummary}
          />
        ) : null}
      </div>

      <p className="text-xs text-on-surface-variant">
        Astuce : utilisez la recherche globale (barre du haut) pour filtrer par nom, téléphone, matricule, filière ou code cohorte.
        Cliquez sur <strong>Voir</strong> pour le détail complet du dossier.
      </p>

      {detailRow ? <InscriptionDetailModal row={detailRow} onClose={() => setDetailRow(null)} /> : null}
    </AdminPageShell>
  );
}
